// HistorialView.jsx
import React, { useMemo } from "react";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";

// helper para formatear la fecha del encabezado: "14 Jun, 2025"
const formatHeaderDate = (isoString) => {
  const raw = format(parseISO(isoString), "d MMM yyyy", { locale: es });
  const parts = raw.split(" "); // ["14", "jun", "2025"] normalmente

  if (parts.length !== 3) return raw;

  const [day, monthRaw, year] = parts;
  const month = monthRaw.charAt(0).toUpperCase() + monthRaw.slice(1); // Jun, Jul, Ago...

  return `${day} ${month}, ${year}`; // "14 Jun, 2025"
};

export default function HistorialView({ historyEvents = [] }) {
  const eventsByDate = useMemo(() => {
    return historyEvents.reduce((acc, ev) => {
      const day = formatHeaderDate(ev.timestamp);
      if (!acc[day]) acc[day] = [];
      acc[day].push(ev);
      return acc;
    }, {});
  }, [historyEvents]);

  const sortedDates = useMemo(
    () =>
      Object.keys(eventsByDate).sort(
        (a, b) =>
          parseISO(eventsByDate[b][0].timestamp) -
          parseISO(eventsByDate[a][0].timestamp)
      ),
    [eventsByDate]
  );

  if (sortedDates.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-gray-400 italic">
        No hay historial de eventos por el momento.
      </div>
    );
  }

  return (
    <div className="historial-container">
      {sortedDates.map((date) => (
        <div key={date} className="historial-row">
          <div className="historial-date">{date}</div>
          <div className="historial-point" />
          <div className="historial-events">
            {eventsByDate[date]
              .sort((a, b) => parseISO(a.timestamp) - parseISO(b.timestamp))
              .map((ev) => {
                const time = format(parseISO(ev.timestamp), "HH:mm");
                const detalles =
                  typeof ev.details === "string"
                    ? ev.details.split(/;\s*/)
                    : [];

                // formatea sólo la parte de "fechaOrden"
                const formateaFechaOrden = (texto) => {
                  const [, tail] = texto.split("fechaOrden:");
                  if (!tail) return texto;
                  const [oldIso, newIso] = tail.split("→").map((s) => s.trim());
                  const fmt = (iso) =>
                    format(parseISO(iso), "dd MMM yy, HH:mm", { locale: es });
                  return `Fecha del pedido actualizada: ${fmt(oldIso)} → ${fmt(
                    newIso
                  )}.`;
                };

                // formatea sólo la parte de "productosSeleccionados"
                const formateaProductos = (texto) => {
                  const parts = texto.split("productosSeleccionados:");
                  if (parts.length < 2) return null; // no hay datos de productos

                  // payload tiene " [oldJSON] → [newJSON]"
                  const payload = parts[1].trim();
                  if (!payload.includes("→")) return null;

                  let [oldJson, newJson] = payload
                    .split("→")
                    .map((s) => s.trim());

                  try {
                    const oldArr = JSON.parse(oldJson);
                    const newArr = JSON.parse(newJson);

                    // si no hubo cambio, salimos
                    if (JSON.stringify(oldArr) === JSON.stringify(newArr)) {
                      return null;
                    }

                    // extraemos nombres únicos de la nueva versión
                    const nombresUnicos = Array.from(
                      new Set(newArr.map((p) => p.nombre))
                    );

                    if (nombresUnicos.length === 0) {
                      return "Productos actualizados.";
                    }

                    let resumenNombres;
                    if (nombresUnicos.length <= 2) {
                      resumenNombres = nombresUnicos.join(", ");
                    } else {
                      resumenNombres = `${nombresUnicos
                        .slice(0, 2)
                        .join(", ")} y ${nombresUnicos.length - 2} más`;
                    }

                    return `Productos actualizados (${nombresUnicos.length}): ${resumenNombres}.`;
                  } catch {
                    return "Productos actualizados."; // JSON mal formado, pero sin spamear
                  }
                };

                // formatea sólo la parte de "envio"
                const formateaEnvio = (texto) => {
                  const parts = texto.split("envio:");
                  if (parts.length < 2) return texto;

                  const payload = parts[1].trim();
                  if (!payload.includes("→")) {
                    return "Datos de envío actualizados.";
                  }

                  let [oldJson, newJson] = payload
                    .split("→")
                    .map((s) => s.trim());

                  try {
                    const oldObj = JSON.parse(oldJson);
                    const newObj = JSON.parse(newJson);

                    const cambios = [];

                    if (oldObj.metodo !== newObj.metodo) {
                      cambios.push(
                        `método ${oldObj.metodo} → ${newObj.metodo}`
                      );
                    }
                    if (oldObj.paqueteria !== newObj.paqueteria) {
                      cambios.push(
                        `paquetería ${oldObj.paqueteria} → ${newObj.paqueteria}`
                      );
                    }
                    if (oldObj.numeroGuia !== newObj.numeroGuia) {
                      cambios.push("número de guía actualizado");
                    }
                    if (oldObj.direccion !== newObj.direccion) {
                      cambios.push("dirección de envío actualizada");
                    }
                    if (oldObj.fechaEnvio !== newObj.fechaEnvio) {
                      try {
                        const fOld = format(
                          parseISO(oldObj.fechaEnvio),
                          "dd MMM yy",
                          { locale: es }
                        );
                        const fNew = format(
                          parseISO(newObj.fechaEnvio),
                          "dd MMM yy",
                          { locale: es }
                        );
                        cambios.push(`fecha de envío ${fOld} → ${fNew}`);
                      } catch {
                        // si la fecha viene rara, la ignoramos
                      }
                    }

                    if (!cambios.length) return null;

                    return `Envío actualizado: ${cambios.join(", ")}.`;
                  } catch {
                    return "Datos de envío actualizados.";
                  }
                };

                // aplicamos formateos
                const cambiosFormateados = detalles
                  .map((c) => {
                    if (c.startsWith("fechaOrden"))
                      return formateaFechaOrden(c);
                    if (c.startsWith("productosSeleccionados"))
                      return formateaProductos(c);
                    if (c.startsWith("envio")) return formateaEnvio(c);
                    return c;
                  })
                  // eliminamos null, strings vacíos y duplicados
                  .filter((c, i, arr) => c && arr.indexOf(c) === i);

                return (
                  <div key={ev.id} className="historial-event-row">
                    <div className="event-time-cell">
                      <i className="bi bi-clock icon" />
                      <span>{time}</span>
                    </div>
                    <div className="event-desc-cell">
                      <ul className="mt-2 list-disc list-inside space-y-1 text-gray-600">
                        {cambiosFormateados.map((c, i) => (
                          <li key={i} className="text-sm">
                            {c}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      ))}
    </div>
  );
}
